import ClaudeService from "./claudeService";

const PENCIL_RE = /\bpencil(?:led|ed|ing)?\s+(?:in|it|that|them|this|something|some)\b/i;
const ACTION_RE = /\b(?:i['’]ll|i will|i['’]ve|i have|i['’]m going to|let me)\s+(?:also\s+)?(?:add|put|move|remove|cancel|delete|confirm|schedule|reschedule|block|book|drop|shift)\b/i;
const CALENDAR_WORD_RE = /\b(?:calendar|event|meeting|appointment|call|lunch|dinner|slot|standup|retro|tomorrow|morning|afternoon|evening|tonight|monday|tuesday|wednesday|thursday|friday|saturday|sunday|\d{1,2}(?::\d{2})?\s?(?:am|pm)|at \d{1,2}(?::\d{2})?)\b/i;

const PLAN_RE = /\b(?:here['’]s your (?:day|plan)|(?:i['’]ve|i have|i['’]ll|i will|let me)\s+(?:lay|laid|plan|planned|map|mapped|arrange|arranged|reshuffle|reshuffled|shuffle|shuffled|put together|block|blocked)\b[^.?!]{0,40}\b(?:day|plan|tomorrow|today|morning|afternoon))/i;

const hasCalendarUpdate = (parsed) => (parsed?.surface_updates || []).some((u) => u?.surface === "calendar" || u?.surface === "my_day");

export function promisesUnsentCalendarWrite(parsed) {
  if (!parsed || hasCalendarUpdate(parsed)) return false;
  if ((parsed.surface_updates || []).length || parsed.desk_action || parsed.deskWriteUnsent) return false;
  const text = String(parsed.chat_response || "");
  return PENCIL_RE.test(text) || PLAN_RE.test(text) || (ACTION_RE.test(text) && CALENDAR_WORD_RE.test(text));
}

export const promisesPlan = (parsed) => PLAN_RE.test(String(parsed?.chat_response || ""));

const NUDGE =
  "\n\n[NeuroQuest app: answer with the JSON object only, and if you are adding, pencilling in, moving, removing or " +
  "confirming a calendar event, or laying out, reshuffling, accepting or dismissing a My Day plan, put that update in surface_updates.]";

const withNudge = (messages) => {
  const list = [...(messages || [])];
  const last = list[list.length - 1];
  if (last?.role === "user" && typeof last.content === "string") list[list.length - 1] = { ...last, content: last.content + NUDGE };
  return list;
};

async function askAgain(callOptions, correction) {
  try {
    const retry = await ClaudeService.sendStructuredMessage({
      ...callOptions,
      system: callOptions.system + correction,
      messages: withNudge(callOptions.messages),
    });
    return retry?.success ? retry.data : null;
  } catch {
    return null;
  }
}

const CORRECTION =
  "\n\nCORRECTION: Your previous reply to this message told the user you would add, pencil in, move, remove or confirm " +
  "a calendar event, but surface_updates had no calendar update, so nothing reached the calendar. " +
  "If you are making that change, reply again and include the calendar update in surface_updates " +
  '(surface "calendar" with type "event", "remove" or "confirm", as the CALENDAR rules describe; your own ideas use "proposed": true). ' +
  "If you are not making a change, reword chat_response so it does not say you will.";

const PLAN_CORRECTION =
  "\n\nCORRECTION: Your previous reply to this message told the user you laid out, reshuffled, accepted or dismissed " +
  "a My Day plan, but surface_updates had no my_day update, so nothing reached My Day. If you are doing that, reply again " +
  'and include it in surface_updates (surface "my_day" with type "plan", "accept", "dismiss" or "day_end", as the MY DAY rules describe). ' +
  "If you are not, reword chat_response so it does not say you did.";

export async function withCalendarRetry(callOptions, parsed) {
  if (!promisesUnsentCalendarWrite(parsed)) return parsed;
  if (process.env.NODE_ENV === "development") {
    console.warn("[Lumen] reply promises a calendar change but sent none — asking again", parsed);
  }
  const planPromise = promisesPlan(parsed);
  const retry = await askAgain(callOptions, planPromise ? PLAN_CORRECTION : CORRECTION);
  if (retry && hasCalendarUpdate(retry)) return retry;
  if (retry && !promisesUnsentCalendarWrite(retry)) return retry;
  return { ...parsed, calendarWriteUnsent: planPromise ? "my_day" : "calendar" };
}
